'use client';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { useDebounce } from '@/hooks/useDebounce';
import { PlaceService } from '@/services/PlaceService';
import type { AddressType } from '@/types/address';
import { Command as CommandPrimitive } from 'cmdk';
import isEqual from 'lodash/isEqual';
import { Delete, Loader2, Pencil } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import useSWR from 'swr';
import AddressDialog from './address-dialog';

interface AddressAutoCompleteProps {
  address: AddressType;
  setAddress: (address: AddressType) => void;
  searchInput: string;
  setSearchInput: (searchInput: string) => void;
  dialogTitle: string;
  showInlineError?: boolean;
  placeholder?: string;
  autoComplete?: string;
  'data-form-type'?: string;
}

export default function AddressAutoComplete(props: AddressAutoCompleteProps) {
  const {
    address,
    setAddress,
    searchInput,
    setSearchInput,
    dialogTitle,
    showInlineError = true,
    placeholder,
    autoComplete,
    'data-form-type': dataFormType,
  } = props;

  const [selectedPlaceId, setSelectedPlaceId] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const debouncedSearchInput = useDebounce(searchInput, 500);

  const { data: predictions = [], isLoading: isPredictionsLoading } = useSWR(
    debouncedSearchInput ? ['predictions', debouncedSearchInput] : null,
    () => PlaceService.getPlacePredictions(debouncedSearchInput),
    {
      revalidateOnFocus: false,
    },
  );

  const { data: placeDetails, isLoading: isDetailsLoading } = useSWR(
    selectedPlaceId ? ['placeDetails', selectedPlaceId] : null,
    () => PlaceService.getPlaceDetails(selectedPlaceId),
    {
      revalidateOnFocus: false,
    },
  );

  const handlePlaceSelect = useCallback((placeId: string) => {
    setSelectedPlaceId(placeId);
    setIsOpen(true);
  }, []);

  useEffect(() => {
    if (placeDetails && !isEqual(placeDetails, address)) {
      setAddress(placeDetails);
    }
  }, [placeDetails, address, setAddress]);

  const isLoading = isPredictionsLoading || isDetailsLoading;

  return (
    <>
      {selectedPlaceId !== '' || address?.formattedAddress ? (
        <div className="flex items-center gap-2">
          <Input value={address.formattedAddress} readOnly />
          <AddressDialog
            isLoading={isLoading}
            dialogTitle={dialogTitle}
            address={address}
            setAddress={setAddress}
            open={isOpen}
            setOpen={setIsOpen}
          >
            <Button
              disabled={isLoading}
              size="icon"
              variant="outline"
              className="shrink-0"
            >
              <Pencil className="size-4" />
            </Button>
          </AddressDialog>
          <Button
            type="reset"
            onClick={() => {
              setSelectedPlaceId('');
              setAddress({
                address1: '',
                address2: '',
                formattedAddress: '',
                city: '',
                region: '',
                postalCode: '',
                country: '',
                lat: 0,
                lng: 0,
              });
            }}
            size="icon"
            variant="outline"
            className="shrink-0"
          >
            <Delete className="size-4" />
          </Button>
        </div>
      ) : (
        <Command shouldFilter={false} className="overflow-visible">
          <div className="flex w-full items-center justify-between rounded-lg border bg-background ring-offset-background text-sm focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
            <CommandPrimitive.Input
              value={searchInput}
              onValueChange={setSearchInput}
              placeholder={placeholder || 'Enter address'}
              className="w-full p-3 rounded-lg outline-none"
              autoComplete={autoComplete}
              data-form-type={dataFormType}
            />
          </div>
          {searchInput !== '' &&
            !isOpen &&
            !selectedPlaceId &&
            showInlineError && (
              <div className="pt-1 text-sm text-red-500">
                Select a valid address from the list
              </div>
            )}

          {searchInput !== '' && (
            <div className="relative animate-in fade-in-0 zoom-in-95 h-auto">
              <CommandList>
                <div className="absolute top-1.5 z-50 w-full">
                  <CommandGroup className="relative h-auto z-50 min-w-[8rem] overflow-hidden rounded-md border shadow-md bg-background">
                    {isPredictionsLoading ? (
                      <div className="h-28 flex items-center justify-center">
                        <Loader2 className="size-6 animate-spin" />
                      </div>
                    ) : (
                      <>
                        {predictions.map((prediction) => (
                          <CommandPrimitive.Item
                            value={prediction.description}
                            onSelect={() => {
                              setSearchInput('');
                              handlePlaceSelect(prediction.place_id);
                            }}
                            className="flex select-text flex-col cursor-pointer gap-0.5 h-max p-2 px-3 rounded-md aria-selected:bg-accent aria-selected:text-accent-foreground hover:bg-accent hover:text-accent-foreground items-start"
                            key={prediction.place_id}
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            {prediction.description}
                          </CommandPrimitive.Item>
                        ))}
                      </>
                    )}

                    <CommandEmpty>
                      {!isPredictionsLoading && predictions.length === 0 && (
                        <div className="py-4 flex items-center justify-center">
                          {searchInput === ''
                            ? 'Please enter an address'
                            : 'No address found'}
                        </div>
                      )}
                    </CommandEmpty>
                  </CommandGroup>
                </div>
              </CommandList>
            </div>
          )}
        </Command>
      )}
    </>
  );
}
